import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import SearchInput from '@ui/search-input/search-input';
import { selectRecordings, selectUploadedVideos } from '@/store/slices/content/selectors';
import { clearRecordings, clearUploadedVideos, clearUploadJobs, createUploadJob } from '@/store/slices/content';
import { getActiveChannel } from '@/store/slices/user/selectors';
import RecordedVideosGrid from './recorded-videos-grid';
import Uploader from '@ui/uploads/uploader/uploader';
import { StaticVideo } from '@/types/video.types';
import useLoadVideos from './use-load-videos';

type TManageVodContentModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (videoId: string, playbackUrl?: string | null) => void;
  currentVideoId?: string | null;
  currentVideoPlaybackUrl?: string | null;
};

const ManageVodContentModal = ({
  open,
  onClose,
  onSave,
  currentVideoId,
  currentVideoPlaybackUrl,
}: TManageVodContentModalProps) => {
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(currentVideoId ?? null);
  const [selectedPlaybackUrl, setSelectedPlaybackUrl] = useState<string | null | undefined>(currentVideoPlaybackUrl);
  const currentVideoIdRef = useRef(currentVideoId);
  currentVideoIdRef.current = currentVideoId;
  const [searchTerm, setSearchTerm] = useState('');
  const [optimisticallyAddedVideos, setOptimisticallyAddedVideos] = useState<StaticVideo[]>([]);
  const [disabled, setDisabled] = useState(false);

  const dispatch = useAppDispatch();
  const activeChannel = useAppSelector(getActiveChannel);
  const recordings = useAppSelector(selectRecordings);
  const uploaded = useAppSelector(selectUploadedVideos);

  const { fetchVideos, loading } = useLoadVideos();

  useEffect(() => {
    if (selectedVideoId || !currentVideoPlaybackUrl) return;

    const selectedRecording = recordings?.find((rec) => rec.playback_url === currentVideoPlaybackUrl);
    if (selectedRecording) {
      setSelectedVideoId(selectedRecording.uuid);
      return;
    }

    const selectedUploaded = uploaded?.find((rec) => rec.playback_url === currentVideoPlaybackUrl);
    if (selectedUploaded) {
      setSelectedVideoId(selectedUploaded.uuid);
    }
  }, [currentVideoPlaybackUrl, selectedVideoId, recordings, uploaded]);

  const filteredRecordings = useMemo(() => {
    // combine all videos and deduplicate by uuid
    const allVideosWithDuplicates = [...(recordings || []), ...(uploaded || []), ...optimisticallyAddedVideos];
    const allVideos = Array.from(new Map(allVideosWithDuplicates.map((vid) => [vid.uuid, vid])).values());

    if (!searchTerm) return allVideos;

    const lowerSearch = searchTerm.toLowerCase();
    return allVideos.filter((video) => video.name?.toLowerCase().includes(lowerSearch));
  }, [recordings, uploaded, optimisticallyAddedVideos, searchTerm]);

  const toggleVideoSelection = (videoId: string, playbackUrl?: string) => {
    if (!disabled) {
      setSelectedVideoId((prev) => (prev === videoId ? null : videoId));
      setSelectedPlaybackUrl(playbackUrl);
    }
  };

  const handleSave = useCallback(() => {
    if (selectedVideoId) {
      onSave(selectedVideoId, selectedPlaybackUrl);
      onClose();
    }
  }, [onClose, onSave, selectedVideoId, selectedPlaybackUrl]);

  const onFile = () => {
    dispatch(
      createUploadJob({
        onStarted: () => {
          setDisabled(true);
        },
        onComplete: (video: StaticVideo) => {
          // optimistically add the video to the list
          setOptimisticallyAddedVideos((prev) => [...prev, video]);
          setDisabled(false);
        },
      })
    );
  };

  useEffect(() => {
    if (open && activeChannel) {
      dispatch(clearRecordings());
      dispatch(clearUploadedVideos());
      fetchVideos();
      setOptimisticallyAddedVideos([]);
      setSelectedVideoId(currentVideoIdRef.current ?? null);
    }
  }, [activeChannel, dispatch, fetchVideos, open]);

  useEffect(() => {
    if (!open) {
      dispatch(clearUploadJobs());
    }
  }, [dispatch, open]);

  const RenderHeaderContent = useCallback(
    () => (
      <Container
        background="none"
        className="flex ai-center jc-space-between padding-t-24 padding-b-16 padding-l-40 padding-r-40"
      >
        <Text className="H4">Add video</Text>
      </Container>
    ),
    []
  );

  const RenderFooterContent = useCallback(
    () => (
      <Container className="flex fd-row jc-flex-end ai-center gap-16 padding-b-24 padding-r-40">
        <Button onClick={onClose} design="LocalBttnSec" className="L1" width="95px" height="48px" disabled={disabled}>
          Cancel
        </Button>
        <Button
          backgroundColor={getThemeColorCSSVariable()}
          height="48px"
          minWidth="81px"
          disabled={!selectedVideoId || selectedVideoId === currentVideoId || disabled}
          disabledOpacity={0.5}
          onClick={handleSave}
          className="flex ai-center jc-center"
        >
          <Text
            color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            className="L1"
          >
            Add
          </Text>
        </Button>
      </Container>
    ),
    [onClose, handleSave, disabled, selectedVideoId, currentVideoId]
  );

  return (
    <>
      <Modal
        showLoadingIndicator={loading}
        data-component="ManageVodContentModal"
        focusLockProps={{ returnFocus: false }}
        open={open}
        customSize={{
          width: '640px',
          height: 'auto',
          maxHeight: '866px',
          maxWidth: '90vw',
        }}
        design="GlobalCont1"
        RenderHeaderContent={RenderHeaderContent}
        RenderFooterContent={RenderFooterContent}
      >
        <Container className="flex-column padding-h-40 padding-v-24 overflow-hidden" height="100%">
          <Container className="flex ai-center gap-10">
            <Container className="flex-1">
              <SearchInput
                width="100%"
                onSubmit={(value) => {
                  setSearchTerm(value);
                }}
                inputProps={{
                  disabled: disabled,
                }}
                debounceTime={500}
                isClearable
                onClear={() => {
                  setSearchTerm('');
                }}
              />
            </Container>
            <Text className="L2">or</Text>
            <Button
              onClick={onFile}
              design="LocalBttnSec"
              className="L1"
              height="32px"
              padding="0 12px"
              borderRadius="100px"
              disabled={disabled}
            >
              Upload video
            </Button>
          </Container>

          <RecordedVideosGrid
            videos={filteredRecordings}
            selectedVideoIds={selectedVideoId ? new Set([selectedVideoId]) : new Set()}
            onToggleSelection={toggleVideoSelection}
          />
        </Container>
        <Uploader />
      </Modal>
    </>
  );
};

export default ManageVodContentModal;
